/**
 * Google Calendar API Client
 *
 * Centralized HTTP client configuration for Google Calendar API interactions
 * following NotePlan Web service abstraction patterns.
 */

import axios, { AxiosInstance } from 'axios'
import { determineBaseURL } from '../../utils/backend'
import { trackError } from '../analytics'
import { ServiceConfig } from './types'

// ============================================================================
// Enhanced Error Handling with Context
// ============================================================================

/**
 * Enhanced error handler that extracts context from axios config
 */
function handleGoogleApiErrorWithContext(error: unknown): never {
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as {
      response: { status: number; statusText: string; data: unknown }
      config?: { url?: string; metadata?: Record<string, unknown> }
    }
    trackError('GoogleApi.request', 'Google API Error', {
      status: axiosError.response.status,
      statusText: axiosError.response.statusText,
      url: axiosError.config?.url,
      data: axiosError.response.data,
      ...axiosError.config?.metadata,
      timestamp: new Date().toISOString(),
    })
  } else if (error && typeof error === 'object' && 'request' in error) {
    const networkError = error as {
      request: unknown
      config?: { url?: string; metadata?: Record<string, unknown> }
      message?: string
    }
    trackError('GoogleApi.request', 'Google API Network Error', {
      url: networkError.config?.url,
      message: networkError.message,
      ...networkError.config?.metadata,
      timestamp: new Date().toISOString(),
    })
  } else {
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown error'
    trackError('GoogleApi.request', errorMessage, {
      timestamp: new Date().toISOString(),
    })
  }

  throw error instanceof Error ? error : new Error(String(error))
}

// ============================================================================
// Client Configuration
// ============================================================================

const DEFAULT_CONFIG: ServiceConfig = {
  baseURL: determineBaseURL('google'),
  timeout: 30_000, // 30 seconds
  retries: 0, // will be handled by React Query
}

// ============================================================================
// HTTP Client Instance with Enhanced Interceptors
// ============================================================================

export const googleApiClient: AxiosInstance = axios.create({
  baseURL: DEFAULT_CONFIG.baseURL,
  timeout: DEFAULT_CONFIG.timeout,
  headers: {
    'Content-Type': 'application/json',
  },
})

// ============================================================================
// Request Interceptors
// ============================================================================

googleApiClient.interceptors.request.use(
  (config) => {
    return config
  },
  (error: unknown) => {
    return Promise.reject(
      error instanceof Error ? error : new Error(String(error))
    )
  }
)

// ============================================================================
// Response Interceptors
// ============================================================================

googleApiClient.interceptors.response.use(
  (response) => {
    return response
  },
  (error: unknown) => {
    handleGoogleApiErrorWithContext(error)
  }
)
